import { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import './Referrals.css';

const API_URL  = process.env.REACT_APP_API_URL  || 'http://localhost:5000';
const ECOM_URL = process.env.REACT_APP_ECOM_URL || 'https://prebook.thenewluxury.in';

const encodeRef = (payload) =>
  btoa(unescape(encodeURIComponent(JSON.stringify(payload))));

const buildReferralLink = (partner) => {
  const payload = { rid: partner.id, rn: partner.name };
  return `${ECOM_URL}/register?ref=${encodeURIComponent(encodeRef(payload))}`;
};

const TYPE_LABEL = { P: 'Promoter', S: 'Store Partner' };
const TYPE_CLASS  = { P: 'ref-badge--promoter', S: 'ref-badge--store' };

const fmt = (n) =>
  `₹${parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/* ── Link New modal ──────────────────────────────────────── */
const LinkNewModal = ({ partner, auth, onClose, onLinked }) => {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch]       = useState('');
  const [loading, setLoading]     = useState(true);
  const [linking, setLinking]     = useState(null);
  const [error, setError]         = useState('');

  const load = useCallback(async (q) => {
    setLoading(true);
    setError('');
    try {
      const params = q ? { search: q } : {};
      const res = await axios.get(`${API_URL}/online-admin/unlinked-customers`, { params, ...auth });
      setCustomers(res.data);
    } catch {
      setError('Failed to load customers.');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { load(''); }, []); // eslint-disable-line

  const handleSearch = (e) => { setSearch(e.target.value); load(e.target.value); };

  const handleLink = async (customer) => {
    setLinking(customer.id);
    setError('');
    try {
      await axios.post(
        `${API_URL}/online-admin/partners/${partner.id}/link/${customer.id}`,
        {},
        auth
      );
      setCustomers(prev => prev.filter(c => c.id !== customer.id));
      onLinked(customer);
    } catch {
      setError('Failed to link customer.');
    } finally {
      setLinking(null);
    }
  };

  return (
    <div className="ref-overlay" onClick={onClose}>
      <div className="ref-modal" onClick={e => e.stopPropagation()}>
        <div className="ref-modal-header">
          <h3 className="ref-modal-title">Link Customer to {partner.name}</h3>
          <button className="ref-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="ref-modal-search-row">
          <input
            className="ref-search-input"
            type="text"
            placeholder="Search name, phone or email…"
            value={search}
            onChange={handleSearch}
          />
        </div>

        {error && <p className="ref-modal-error">{error}</p>}

        <div className="ref-modal-body">
          {loading ? (
            <p className="ref-loading">Loading…</p>
          ) : customers.length === 0 ? (
            <p className="ref-empty">No unlinked customers found.</p>
          ) : (
            <table className="ref-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {customers.map(c => (
                  <tr key={c.id}>
                    <td className="ref-name">{c.name}</td>
                    <td>{c.phone || '—'}</td>
                    <td>{c.email || '—'}</td>
                    <td>
                      <button
                        className="ref-action-btn ref-action-btn--link"
                        onClick={() => handleLink(c)}
                        disabled={linking === c.id}
                      >
                        {linking === c.id ? '…' : 'Link'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

/* ── Referred customers modal ────────────────────────────── */
const ReferredModal = ({ partner, auth, canManage, onClose, onCountChange }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [unlinking, setUnlinking] = useState(null);
  const [error, setError]         = useState('');
  const [showLinkNew, setShowLinkNew] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(
        `${API_URL}/online-admin/partners/${partner.id}/customers`,
        auth
      );
      setCustomers(res.data);
    } catch {
      setError('Failed to load referred customers.');
    } finally {
      setLoading(false);
    }
  }, [partner.id]); // eslint-disable-line

  useEffect(() => { load(); }, []); // eslint-disable-line

  const handleUnlink = async (customer) => {
    setUnlinking(customer.id);
    setError('');
    try {
      await axios.delete(
        `${API_URL}/online-admin/partners/${partner.id}/unlink/${customer.id}`,
        auth
      );
      const updated = customers.filter(c => c.id !== customer.id);
      setCustomers(updated);
      onCountChange(updated.length);
    } catch {
      setError('Failed to unlink customer.');
    } finally {
      setUnlinking(null);
    }
  };

  const handleLinked = (customer) => {
    const updated = [...customers, customer];
    setCustomers(updated);
    onCountChange(updated.length);
    setShowLinkNew(false);
  };

  return (
    <>
      <div className="ref-overlay" onClick={onClose}>
        <div className="ref-modal" onClick={e => e.stopPropagation()}>
          <div className="ref-modal-header">
            <h3 className="ref-modal-title">
              Referred Customers — {partner.name}
              <span className="ref-modal-count">{customers.length}</span>
            </h3>
            <div className="ref-modal-header-actions">
              {canManage && (
                <button
                  className="ref-action-btn ref-action-btn--new"
                  onClick={() => setShowLinkNew(true)}
                >
                  + Link New
                </button>
              )}
              <button className="ref-modal-close" onClick={onClose}>✕</button>
            </div>
          </div>

          {error && <p className="ref-modal-error">{error}</p>}

          <div className="ref-modal-body">
            {loading ? (
              <p className="ref-loading">Loading…</p>
            ) : customers.length === 0 ? (
              <p className="ref-empty">No referred customers yet.</p>
            ) : (
              <table className="ref-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Email</th>
                    {canManage && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {customers.map(c => (
                    <tr key={c.id}>
                      <td className="ref-name">{c.name}</td>
                      <td>{c.phone || '—'}</td>
                      <td>{c.email || '—'}</td>
                      {canManage && (
                        <td>
                          <button
                            className="ref-action-btn ref-action-btn--unlink"
                            onClick={() => handleUnlink(c)}
                            disabled={unlinking === c.id}
                          >
                            {unlinking === c.id ? '…' : 'Unlink'}
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {showLinkNew && (
        <LinkNewModal
          partner={partner}
          auth={auth}
          onClose={() => setShowLinkNew(false)}
          onLinked={handleLinked}
        />
      )}
    </>
  );
};

/* ── Partner Payments modal ──────────────────────────────── */
const PartnerPaymentsModal = ({ partner, auth, onClose }) => {
  const [weeks, setWeeks]     = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const [expandedWeek, setExpandedWeek]               = useState(null);
  const [expandedHistoryWeek, setExpandedHistoryWeek] = useState(null);
  const [expandedOrder, setExpandedOrder]             = useState(null);
  const [orderDetails, setOrderDetails]               = useState({});
  const [orderDetailsLoading, setOrderDetailsLoading] = useState({});

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = { user_id: partner.id };
        const [sumRes, histRes] = await Promise.all([
          axios.get(`${API_URL}/pay/admin/partner-weekly-summary`, { params, ...auth }),
          axios.get(`${API_URL}/pay/admin/partner-weekly-payment-history`, { params, ...auth }),
        ]);
        setWeeks(sumRes.data);
        setHistory(histRes.data);
      } catch {
        setError('Could not load payments.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [partner.id]); // eslint-disable-line

  const fmtPrice = (n) => n != null ? `₹${parseFloat(n).toFixed(2)}` : '—';
  const fmtDate  = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
  const fmtWeekLabel = (s, e) => `${fmtDate(s)} – ${fmtDate(e)}`;

  const toggleOrder = async (orderId) => {
    if (expandedOrder === orderId) { setExpandedOrder(null); return; }
    setExpandedOrder(orderId);
    if (!orderDetails[orderId]) {
      setOrderDetailsLoading(prev => ({ ...prev, [orderId]: true }));
      try {
        const { data } = await axios.get(`${API_URL}/sales-orders/${orderId}/commission`, auth);
        setOrderDetails(prev => ({ ...prev, [orderId]: data }));
      } catch {
        setOrderDetails(prev => ({ ...prev, [orderId]: null }));
      } finally {
        setOrderDetailsLoading(prev => ({ ...prev, [orderId]: false }));
      }
    }
  };

  const renderOrderList = (orders) => (
    <div className="pmt-orders">
      {(orders || []).map(order => {
        const orderOpen  = expandedOrder === order.id;
        const detail     = orderDetails[order.id];
        const detLoading = orderDetailsLoading[order.id];
        return (
          <div key={order.id} className="pmt-order-row">
            <div className="pmt-order-header" onClick={() => toggleOrder(order.id)}>
              <div className="pmt-order-left">
                <span className="pmt-order-no">{order.order_no}</span>
                <span className="pmt-order-date">{fmtDate(order.delivery_date || order.order_date)}</span>
              </div>
              <div className="pmt-order-right">
                <span className="pmt-order-amount">{fmtPrice(order.order_amount)}</span>
                {order.commission > 0 && (
                  <span className="pmt-order-comm">−{fmtPrice(order.commission)}</span>
                )}
                <span className="pmt-chevron-sm">{orderOpen ? '▲' : '▼'}</span>
              </div>
            </div>
            {orderOpen && (
              <div className="pmt-lines">
                {detLoading && <p className="pmt-lines-msg">Loading…</p>}
                {!detLoading && !detail && <p className="pmt-lines-msg pmt-lines-err">Failed to load details.</p>}
                {!detLoading && detail && (
                  <table className="pmt-lines-table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th className="pmt-num">Qty</th>
                        <th className="pmt-num">Price</th>
                        <th className="pmt-num">Pre-GST</th>
                        <th className="pmt-num">Comm%</th>
                        <th className="pmt-num">Commission</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.lines.map(line => (
                        <tr key={line.line_id}>
                          <td>{line.product_name}</td>
                          <td className="pmt-num">{line.qty}</td>
                          <td className="pmt-num">{fmtPrice(line.unit_price)}</td>
                          <td className="pmt-num">{fmtPrice(line.pre_gst_unit_price)}</td>
                          <td className="pmt-num">{line.commission_percent}%</td>
                          <td className="pmt-num">{fmtPrice(line.line_commission)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={5} className="pmt-lines-total-label">Order total commission</td>
                        <td className="pmt-num pmt-lines-total">{fmtPrice(detail.total_commission)}</td>
                      </tr>
                    </tfoot>
                  </table>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const renderWeekCard = (week, isHistory) => {
    const isOpen = isHistory
      ? expandedHistoryWeek === week.week_start
      : expandedWeek === week.week_start;
    const toggle = () => {
      if (isHistory) {
        setExpandedHistoryWeek(prev => prev === week.week_start ? null : week.week_start);
      } else {
        setExpandedWeek(prev => prev === week.week_start ? null : week.week_start);
      }
      setExpandedOrder(null);
    };
    return (
      <div key={`${isHistory ? 'h' : 'p'}_${week.week_start}`} className={`pmt-card${isHistory ? ' pmt-card--paid' : ''}`}>
        <div className="pmt-card-header" onClick={toggle}>
          <div className="pmt-card-info">
            <span className="pmt-card-label">
              {fmtWeekLabel(week.week_start, week.week_end)}
              <span className="pmt-chevron">{isOpen ? ' ▲' : ' ▼'}</span>
            </span>
            <span className="pmt-card-meta">
              {week.order_count} order{week.order_count !== 1 ? 's' : ''}
              {isHistory ? ` · Paid on ${fmtDate(week.completed_date)}` : (!week.is_payable && <span className="pmt-pending-note"> · payable on Sunday</span>)}
            </span>
            {week.total_commission > 0 && (
              <span className="pmt-commission-row">
                <span className="pmt-gross">{fmtPrice(week.gross_total)}</span>
                <span className="pmt-commission-deduct">− {fmtPrice(week.total_commission)} commission</span>
              </span>
            )}
          </div>
          <div className="pmt-card-right">
            <span className="pmt-card-total">{fmtPrice(week.net_total)}</span>
            {isHistory
              ? <span className="pmt-paid-badge">Paid</span>
              : <span className={`pmt-status-badge${week.is_payable ? ' pmt-status-badge--due' : ''}`}>
                  {week.is_payable ? 'Due' : 'Pending'}
                </span>
            }
          </div>
        </div>
        {isOpen && renderOrderList(week.orders)}
      </div>
    );
  };

  return (
    <div className="ref-overlay" onClick={onClose}>
      <div className="ref-modal pmt-modal" onClick={e => e.stopPropagation()}>
        <div className="ref-modal-header">
          <h3 className="ref-modal-title">Payments — {partner.name}</h3>
          <button className="ref-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="ref-modal-body pmt-modal-body">
          {loading && <p className="ref-loading">Loading…</p>}
          {error   && <p className="ref-error">{error}</p>}
          {!loading && !error && weeks.length === 0 && history.length === 0 && (
            <p className="ref-empty">No payment records found.</p>
          )}
          {!loading && !error && (
            <div className="pmt-list">
              {weeks.length > 0 && (
                <>
                  <p className="pmt-section-heading">Unpaid Weeks</p>
                  {weeks.map(w => renderWeekCard(w, false))}
                </>
              )}
              {history.length > 0 && (
                <>
                  <p className="pmt-section-heading">Payment History</p>
                  {history.map(w => renderWeekCard(w, true))}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ── Main page ───────────────────────────────────────────── */
const Referrals = () => {
  const { user } = useContext(AuthContext);
  const auth = { headers: { Authorization: `Bearer ${user?.token}` } };

  const [partners, setPartners]       = useState([]);
  const [search, setSearch]           = useState('');
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [paymentsPartner, setPaymentsPartner] = useState(null);
  const [canManage, setCanManage]     = useState(false);
  const [copiedId, setCopiedId]       = useState(null);

  const fetchPartners = useCallback(async (q) => {
    setLoading(true);
    setError('');
    try {
      const params = q ? { search: q } : {};
      const res = await axios.get(`${API_URL}/online-admin/partners`, { params, ...{ headers: auth.headers } });
      setPartners(res.data);
    } catch {
      setError('Failed to load partners.');
    } finally {
      setLoading(false);
    }
  }, [user?.token]); // eslint-disable-line

  useEffect(() => {
    fetchPartners('');
    axios.post(
      `${API_URL}/authRoutes/permissions/check`,
      { permissionName: 'online.referrals.manage' },
      { headers: auth.headers }
    ).then(res => setCanManage(res.data.hasPermission)).catch(() => {});
  }, []); // eslint-disable-line

  const handleSearch = (e) => { setSearch(e.target.value); fetchPartners(e.target.value); };
  const handleReset  = () => { setSearch(''); fetchPartners(''); };

  const copyLink = (partner) => {
    const link = buildReferralLink(partner);
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(link).then(() => {
        setCopiedId(partner.id);
        setTimeout(() => setCopiedId(null), 2000);
      }).catch(() => fallbackCopy(link, partner.id));
    } else {
      fallbackCopy(link, partner.id);
    }
  };

  const fallbackCopy = (text, id) => {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.cssText = 'position:fixed;top:0;left:0;opacity:0;';
    document.body.appendChild(ta);
    ta.focus(); ta.select();
    try { document.execCommand('copy'); setCopiedId(id); setTimeout(() => setCopiedId(null), 2000); } catch {}
    document.body.removeChild(ta);
  };

  const handleCountChange = (partnerId, newCount) => {
    setPartners(prev =>
      prev.map(p => p.id === partnerId ? { ...p, referred_count: newCount } : p)
    );
  };

  return (
    <div className="ref-page">
      <div className="ref-header">
        <div>
          <h1 className="ref-title">Partners</h1>
          <p className="ref-subtitle">Promoters and Store Partners with their referral activity</p>
        </div>
        <button className="ref-refresh-btn" onClick={() => fetchPartners(search)} disabled={loading}>
          ↺ Refresh
        </button>
      </div>

      <div className="ref-search-row">
        <input
          className="ref-search-input"
          type="text"
          placeholder="Search by name, phone or email…"
          value={search}
          onChange={handleSearch}
        />
        {search && (
          <button className="ref-clear-btn" onClick={handleReset}>✕ Clear</button>
        )}
        <span className="ref-count">{partners.length} partner{partners.length !== 1 ? 's' : ''}</span>
      </div>

      {error && <p className="ref-error">{error}</p>}

      {loading ? (
        <p className="ref-loading">Loading…</p>
      ) : partners.length === 0 ? (
        <div className="ref-empty">No partners found{search ? ` for "${search}"` : ''}.</div>
      ) : (
        <div className="ref-table-wrap">
          <table className="ref-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Phone</th>
                <th>Email</th>
                <th className="ref-col-num">Customers</th>
                <th className="ref-col-num">Orders</th>
                <th className="ref-col-num">Order Value</th>
                <th className="ref-col-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {partners.map((p) => (
                <tr key={p.id} className={p.status !== 'A' ? 'ref-row--inactive' : ''}>
                  <td className="ref-name">{p.name}</td>
                  <td>
                    <span className={`ref-badge ${TYPE_CLASS[p.user_type] || ''}`}>
                      {TYPE_LABEL[p.user_type] || p.user_type}
                    </span>
                  </td>
                  <td>{p.phone || '—'}</td>
                  <td>{p.email || '—'}</td>
                  <td className="ref-col-num">
                    <button
                      className="ref-count-link"
                      onClick={() => setSelectedPartner(p)}
                    >
                      {p.referred_count}
                    </button>
                  </td>
                  <td className="ref-col-num">{p.order_count}</td>
                  <td className="ref-col-num">{fmt(p.total_order_amount)}</td>
                  <td className="ref-col-center">
                    <div className="ref-action-cell">
                      <button
                        className="ref-link-btn"
                        title="Copy referral registration link"
                        onClick={() => copyLink(p)}
                      >
                        {copiedId === p.id ? '✓' : '🔗'}
                      </button>
                      <button
                        className="ref-link-btn ref-link-btn--payments"
                        title="View payments"
                        onClick={() => setPaymentsPartner(p)}
                      >
                        ₹
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedPartner && (
        <ReferredModal
          partner={selectedPartner}
          auth={{ headers: auth.headers }}
          canManage={canManage}
          onClose={() => setSelectedPartner(null)}
          onCountChange={(count) => handleCountChange(selectedPartner.id, count)}
        />
      )}

      {paymentsPartner && (
        <PartnerPaymentsModal
          partner={paymentsPartner}
          auth={{ headers: auth.headers }}
          onClose={() => setPaymentsPartner(null)}
        />
      )}
    </div>
  );
};

export default Referrals;
